import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { get, post } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, Modal } from "../../../components/admin/EssUi";
import { Megaphone, BarChart3, CheckCircle2, Search, FileEdit, Eye } from "lucide-react";

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";

const CATEGORIES = [
  "All Categories",
  "HR Notice",
  "Company News",
  "IT Notice",
  "Safety",
  "Events",
  "Finance",
  "Operations"
];

const FORM_CATEGORIES = CATEGORIES.filter((c) => c !== "All Categories");

const INIT_FORM = {
  name: "",
  body: "",
  category: "HR Notice",
  target: "all",
  date_start: "",
  date_end: "",
  is_pinned: false,
  is_published: true,
};

const statusChip = (status) => {
  if (status === "Published") return "ess-chip ess-chip-green";
  if (status === "Scheduled") return "ess-chip ess-chip-orange";
  return "ess-chip ess-chip-gray";
};

const AdminPanelAnnouncementsPage = () => {
  const intl = useIntl();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [audFilter, setAudFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNewForm, setShowNewForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT_FORM);

  const loadData = async () => {
    try {
      const r = await get("/api/announcements");
      setData((r || []).map((e, i) => ({
        id: e.id || i,
        title: e.name || e.title || "—",
        category: e.category || "HR Notice",
        audience: e.target === "department" ? "Department" : e.target === "team" ? "Teams" : "All Employees",
        author: "—",
        published: e.date_start ? new Date(e.date_start.split(" ")[0]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—",
        readRate: e.read_rate ? `${e.read_rate}%` : "—",
        acknowledged: e.acknowledged ? `${e.acknowledged}%` : "—",
        status: e.is_published ? "Published" : e.state === "draft" ? "Draft" : "Scheduled",
      })));
    } catch { setData([]); }
    setLoaded(true);
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (loaded && location.state?.openAdd) {
      setShowNewForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [loaded, location.state?.openAdd]);

  const openNewForm = () => {
    setForm(INIT_FORM);
    setFormError("");
    setShowNewForm(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setFormError("Title is required");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const vals = { name: form.name.trim(), body: form.body.trim(), category: form.category, target: form.target, is_pinned: !!form.is_pinned, is_published: !!form.is_published };
      if (form.date_start) vals.date_start = form.date_start;
      if (form.date_end) vals.date_end = form.date_end;
      await post("/api/announcements", vals);
      setShowNewForm(false);
      setForm(INIT_FORM);
      await loadData();
    } catch (e) {
      setFormError(`Failed to create announcement: ${e?.message || "Unknown error"}`);
    }
    setSaving(false);
  };

  if (!loaded) return <LoadingScreen />;

  const cats = [...new Set(data.map(r=>r.category))];
  const statuses = [...new Set(data.map(r=>r.status))];
  const audiences = [...new Set(data.map(r=>r.audience))];

  const filtered = data.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (catFilter !== "all" && r.category !== catFilter) return false;
    if (audFilter !== "all" && r.audience !== audFilter) return false;
    if (search.trim() && !r.title.toLowerCase().includes(search.toLowerCase()) && !r.author.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const publishedCount = data.filter(r => r.status === "Published").length;
  const draftCount = data.filter(r => r.status === "Draft").length;
  const scheduledCount = data.filter(r => r.status === "Scheduled").length;
  const archivedCount = data.filter(r => r.status === "Archived").length;

  const readRates = data.filter(r => r.readRate !== "—").map(r => parseInt(r.readRate));
  const avgReadRate = readRates.length ? `${Math.round(readRates.reduce((s,n)=>s+n,0)/readRates.length)}%` : "—";
  const ackRates = data.filter(r => r.acknowledged !== "—").map(r => parseInt(r.acknowledged));
  const avgAckRate = ackRates.length ? `${Math.round(ackRates.reduce((s,n)=>s+n,0)/ackRates.length)}%` : "—";

  const tabs = [
    { key: "all", label: "All", count: data.length },
    { key: "published", label: "Published", count: publishedCount },
    { key: "draft", label: "Draft", count: draftCount },
    { key: "scheduled", label: "Scheduled", count: scheduledCount },
    { key: "archived", label: "Archived", count: archivedCount },
  ];

  const getTabData = () => {
    if (activeTab === "published") {
      return filtered.filter(r => r.status === "Published");
    } else if (activeTab === "draft") {
      return filtered.filter(r => r.status === "Draft");
    } else if (activeTab === "scheduled") {
      return filtered.filter(r => r.status === "Scheduled");
    } else if (activeTab === "archived") {
      return filtered.filter(r => r.status === "Archived");
    }
    return filtered;
  };

  const displayData = getTabData();

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader 
        title="Announcements" 
        subtitle="Publish and manage company-wide communications"
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={openNewForm} className="ess-btn ess-btn-primary">
              <Megaphone size={15} />
              New Announcement
            </button>
            <button className="ess-btn ess-btn-secondary">
              <BarChart3 size={15} />
              Analytics
            </button>
          </div>
        }
      />

      <Modal open={showNewForm} onClose={() => setShowNewForm(false)} title="New Announcement" size="lg">
        <div className="space-y-4">
          {formError && (
            <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", padding: "9px 12px", borderRadius: "6px", fontSize: "12.5px" }}>
              {formError}
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Title *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Office closure on 15 August"
              className="ess-inp"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Message</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={4}
              placeholder="Announcement body…"
              className="ess-inp"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'grid', gap: '12px' }} className="ess-rg-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="ess-inp"
                style={{ width: '100%' }}
              >
                {FORM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Audience</label>
              <select
                value={form.target}
                onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                className="ess-inp"
                style={{ width: '100%' }}
              >
                <option value="all">All Employees</option>
                <option value="department">Department</option>
                <option value="team">Teams</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '12px' }} className="ess-rg-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Start date</label>
              <input
                type="date"
                value={form.date_start}
                onChange={(e) => setForm((f) => ({ ...f, date_start: e.target.value }))}
                className="ess-inp"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>End date</label>
              <input
                type="date"
                value={form.date_end}
                onChange={(e) => setForm((f) => ({ ...f, date_end: e.target.value }))}
                className="ess-inp"
                style={{ width: '100%' }}
              />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: GREEN }}
            />
            Publish immediately
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' }}>
            <button onClick={() => setShowNewForm(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="ess-btn ess-btn-primary">
              {saving ? "Saving…" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      <div className="ess-sg ess-sg4" style={{ marginBottom: '20px' }}>
        <StatCard label="Published" value={publishedCount} icon={Megaphone} trend="This quarter" />
        <StatCard label="Drafts" value={draftCount} icon={FileEdit} trend="Pending review" />
        <StatCard label="Avg Read Rate" value={avgReadRate} icon={Eye} />
        <StatCard label="Acknowledgements" value={avgAckRate} icon={CheckCircle2} />
      </div>

      <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', borderBottom: `1px solid ${BORDER}`, overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '9px 2px',
              border: 'none',
              background: 'transparent',
              borderBottom: `2px solid ${activeTab === tab.key ? GREEN : "transparent"}`,
              marginBottom: -1,
              color: activeTab === tab.key ? GREEN : '#64748b',
              fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500',
              cursor: 'pointer',
              transition: 'color 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? '#f0f7f0' : '#f1f5f9', color: activeTab === tab.key ? GREEN : '#64748b', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="ess-fbar" style={{ padding: 16, marginBottom: '20px', background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}` }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search announcement..."
            style={{ width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', outline: 'none', background: '#fff' }}
          />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', background: '#fff', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Categories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={audFilter} onChange={(e) => setAudFilter(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', background: '#fff', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Audience</option>
          {audiences.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13.5px', background: '#fff', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="ess-btn ess-btn-secondary" style={{ whiteSpace: 'nowrap' }}>
          <CheckCircle2 size={15} />
          Requires Acknowledgement
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ess-tbl" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th className="ess-th">Title</th>
                <th className="ess-th">Category</th>
                <th className="ess-th">Audience</th>
                <th className="ess-th">Author</th>
                <th className="ess-th">Published</th>
                <th className="ess-th" style={{ textAlign: 'center' }}>Read Rate</th>
                <th className="ess-th" style={{ textAlign: 'center' }}>Acknowledged</th>
                <th className="ess-th">Status</th>
                <th className="ess-th ess-th-r"></th>
              </tr>
            </thead>
            <tbody>
              {displayData.length === 0 ? (
                <tr><td colSpan={9}><div className="ess-empty">No announcements found.</div></td></tr>
              ) : displayData.map((row) => (
                <tr key={row.id} className="ess-tr">
                  <td className="ess-td" style={{ fontSize: '13.5px', fontWeight: 600, color: '#1e293b' }}>{row.title}</td>
                  <td className="ess-td"><span className="ess-chip ess-chip-gray">{row.category}</span></td>
                  <td className="ess-td" style={{ color: '#475569' }}>{row.audience}</td>
                  <td className="ess-td" style={{ color: '#64748b' }}>{row.author}</td>
                  <td className="ess-td" style={{ color: row.published !== "—" ? '#475569' : '#94a3b8' }}>{row.published}</td>
                  <td className="ess-td" style={{ fontWeight: row.readRate !== "—" ? 600 : 400, color: row.readRate !== "—" ? '#1e293b' : '#94a3b8', textAlign: 'center' }}>{row.readRate || "—"}</td>
                  <td className="ess-td" style={{ fontWeight: row.acknowledged !== "—" ? 600 : 400, color: row.acknowledged !== "—" ? '#1e293b' : '#94a3b8', textAlign: 'center' }}>{row.acknowledged || "—"}</td>
                  <td className="ess-td"><span className={statusChip(row.status)}>{row.status}</span></td>
                  <td className="ess-td ess-th-r">
                    <button style={{ border: 'none', background: 'transparent', color: GREEN, fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      onMouseEnter={(e) => e.target.style.background = '#f0f7f0'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelAnnouncementsPage;